import Mousetrap from 'mousetrap';
import 'mousetrap/plugins/global-bind/mousetrap-global-bind';
import { useEffect } from 'react';
import { useEffectEvent } from 'use-effect-event';

type Key = Parameters<typeof Mousetrap.bind>[0];
type Callback = Parameters<typeof Mousetrap.bind>[1];

type ShortcutOptions<T extends Key> = {
  action?: string;
  global?: boolean;
} & (T extends 'esc' ? { skip: boolean } : { skip?: boolean });

const registeredShortcuts = new Set<string>();

const useShortcut = <T extends Key>(
  key: T,
  callback: Callback,
  { global, action, skip }: ShortcutOptions<T> = { skip: false },
) => {
  const callbackEvent = useEffectEvent(callback);
  const keyJSON = JSON.stringify(key);

  useEffect(() => {
    if (skip) return undefined;

    if (
      process.env.NODE_ENV !== 'production' &&
      !window.IS_SLAB_STORYBOOK &&
      registeredShortcuts.has(keyJSON)
    ) {
      throw new Error(
        `Shortcut ${keyJSON} is registered simultaneously by two components. Ensure that you've provided valid 'skip' values in all the places where it's registered.`,
      );
    }

    const parsedKey = JSON.parse(keyJSON);
    Mousetrap[global ? 'bindGlobal' : 'bind'](parsedKey, callbackEvent, action);
    registeredShortcuts.add(keyJSON);
    return () => {
      Mousetrap.unbind(parsedKey, action);
      registeredShortcuts.delete(keyJSON);
    };
  }, [keyJSON, global, action, skip]);
};

export default useShortcut;
